import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { fetchProfile, updateProfile, type Profile } from '@/api/profile';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { InfoRow } from '@/components/InfoRow';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { showToast } from '@/lib/toast';
import { ProfilePasswordCard } from './ProfilePasswordCard';

interface ProfileEditState {
  name: string;
  lastname: string;
  firstname: string;
  surname: string;
  email: string;
}

function toEditState(p: Profile): ProfileEditState {
  return {
    name: p.name,
    lastname: p.lastname ?? '',
    firstname: p.firstname ?? '',
    surname: p.surname ?? '',
    email: p.email,
  };
}

function upstreamMessage(err: unknown): string | null {
  return isAxiosError<{ message?: string }>(err) ? (err.response?.data?.message ?? null) : null;
}

/**
 * "Настройки профиля" — 1:1 старый Orchid UserProfileScreen (см. докблок
 * ProfileController в Laravel), но в новом дизайне: просмотр + «Редактировать»
 * (тот же паттерн, что «Общее» в ClientDetailView.tsx), а не всегда открытая
 * форма. Смена пароля — отдельная, всегда редактируемая карточка (как и в
 * старом экране — два независимых блока с двумя разными Save).
 */
export function ProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editState, setEditState] = useState<ProfileEditState | null>(null);
  const [saving, setSaving] = useState(false);
  const { alert: alertDialog, dialog } = useConfirmDialog();

  useEffect(() => {
    fetchProfile()
      .then(setProfile)
      .catch(() => setLoadError('Не удалось загрузить профиль.'));
  }, []);

  const startEditing = () => {
    if (!profile) return;
    setEditState(toEditState(profile));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditState(null);
  };

  const saveEditing = async () => {
    if (!editState) return;
    if (!editState.name.trim() || !editState.email.trim()) {
      await alertDialog('Заполните обязательные поля: Название, Электронная почта.');
      return;
    }
    setSaving(true);
    try {
      const fresh = await updateProfile({
        name: editState.name,
        lastname: editState.lastname || null,
        firstname: editState.firstname || null,
        surname: editState.surname || null,
        email: editState.email,
      });
      setProfile(fresh);
      setEditing(false);
      setEditState(null);
      showToast('Профиль сохранён');
    } catch (err) {
      await alertDialog(upstreamMessage(err) || 'Не удалось сохранить изменения.');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <section className="detail">
        <p className="field-error">{loadError}</p>
      </section>
    );
  }

  if (!profile) {
    return (
      <div className="panel-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <section className="detail">
      <header className="detail__header">
        <div className="detail__hero">
          <h1>Настройки профиля</h1>
        </div>
        <div className="detail__header-actions">
          {editing ? (
            <>
              <button type="button" className="btn btn--secondary" onClick={cancelEditing} disabled={saving}>
                Отмена
              </button>
              <button type="button" className="btn btn--primary" onClick={saveEditing} disabled={saving}>
                <Icon name="check" size={15} /> {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary" onClick={startEditing}>
              <Icon name="pencil" size={15} /> Редактировать
            </button>
          )}
        </div>
      </header>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 className="detail__subtitle">Информация профиля</h2>
        {editing && editState ? (
          <div className="edit-fields">
            <label className="edit-field edit-field--readonly">
              <span>Роль</span>
              <input type="text" value={profile.role ?? '–'} disabled readOnly />
            </label>
            <label className="edit-field">
              <span>Название *</span>
              <input
                type="text"
                value={editState.name}
                onChange={(e) => setEditState({ ...editState, name: e.target.value })}
              />
            </label>
            <label className="edit-field">
              <span>Фамилия</span>
              <input
                type="text"
                value={editState.lastname}
                onChange={(e) => setEditState({ ...editState, lastname: e.target.value })}
              />
            </label>
            <label className="edit-field">
              <span>Имя</span>
              <input
                type="text"
                value={editState.firstname}
                onChange={(e) => setEditState({ ...editState, firstname: e.target.value })}
              />
            </label>
            <label className="edit-field">
              <span>Отчество</span>
              <input
                type="text"
                value={editState.surname}
                onChange={(e) => setEditState({ ...editState, surname: e.target.value })}
              />
            </label>
            <label className="edit-field">
              <span>Электронная почта *</span>
              <input
                type="email"
                value={editState.email}
                onChange={(e) => setEditState({ ...editState, email: e.target.value })}
              />
            </label>
          </div>
        ) : (
          <dl className="info-list">
            <InfoRow label="Роль" value={profile.role} />
            <InfoRow label="Название" value={profile.name} />
            <InfoRow label="Фамилия" value={profile.lastname} />
            <InfoRow label="Имя" value={profile.firstname} />
            <InfoRow label="Отчество" value={profile.surname} />
            <InfoRow label="Электронная почта" value={profile.email} />
          </dl>
        )}
      </div>

      <ProfilePasswordCard login={profile.login} />
      {dialog}
    </section>
  );
}
